/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PHASE_1_ALERT_PROFILES
 * Date:         2026-01-28
 * Modified By:  Cursor
 *
 * Description:
 * Phase 1: deleteAlertProfile handles 204 No Content; return { success: true }.
 *
 * Related Files:
 * - client/src/features/setup/alerts/ProfilesList.tsx
 * - server/controllers/alert-profile/updateProfile.js
 */

import axios from "axios";
import { apiUrl } from "../config/environment";
import { getAuthHeaders } from "../utils/authUtils";

const api: string = `${apiUrl}/api/alert-profiles`;

export interface AlertProfile {
  _id: string;
  userId: string;
  name: string;
  mode: "smart";
  isActive: boolean;
  filters: Record<string, any>;
  stats?: {
    lastRunAt?: Date;
    estPerDay?: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateAlertProfileData {
  name: string;
  mode: "smart";
  filters: Record<string, any>;
  isActive?: boolean;
}

export interface UpdateAlertProfileData {
  name?: string;
  mode?: "smart" | "professional";
  filters?: Record<string, any>;
  isActive?: boolean;
}

export interface AlertProfileListResponse {
  success: boolean;
  data: AlertProfile[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface AlertProfileResponse {
  success: boolean;
  data: AlertProfile;
  message?: string;
}

/**
 * Get all alert profiles for the current user
 */
export async function listAlertProfiles(params?: {
  page?: number;
  limit?: number;
}): Promise<AlertProfileListResponse> {
  const { data } = await axios.get(api, {
    params,
    ...getAuthHeaders(),
  });
  return data;
}

/**
 * Get a specific alert profile by ID
 */
export async function getAlertProfileById(id: string): Promise<AlertProfileResponse> {
  const { data } = await axios.get(`${api}/${id}`, getAuthHeaders());
  return data;
}

/**
 * Create a new alert profile
 */
export async function createAlertProfile(
  profile: CreateAlertProfileData,
): Promise<AlertProfileResponse> {
  const { data } = await axios.post(api, profile, getAuthHeaders());
  return data;
}

/**
 * Update an alert profile
 */
export async function updateAlertProfile(
  id: string,
  updates: UpdateAlertProfileData,
): Promise<AlertProfileResponse> {
  const { data } = await axios.patch(`${api}/${id}`, updates, getAuthHeaders());
  return data;
}

/**
 * Delete an alert profile (hard delete). Returns 204 No Content; service returns { success: true }.
 */
export async function deleteAlertProfile(id: string): Promise<{ success: true }> {
  await axios.delete(`${api}/${id}`, getAuthHeaders());
  return { success: true };
}

/**
 * Toggle the active status of an alert profile
 */
export async function toggleAlertProfileStatus(id: string): Promise<AlertProfileResponse> {
  const { data } = await axios.patch(`${api}/${id}/toggle`, {}, getAuthHeaders());
  return data;
}

/**
 * Duplicate an alert profile
 */
export async function duplicateAlertProfile(id: string): Promise<AlertProfileResponse> {
  const { data } = await axios.post(`${api}/${id}/duplicate`, {}, getAuthHeaders());
  return data;
}

